import axios from 'axios';
import { flattenGizmoConfigs } from '../../gizmos/utils';
import {
  IFlowyError,
  IGizmo,
  IIndex,
  IIndexColumn,
  IRealmFilledForm,
  ISingleFeature,
  IUserV2,
} from '../../types';
import Dao from '../dao';
import { Gizmo } from '../../gizmos';

interface IParams {
  type: 'rootGizmo' | 'rootGizmoData';
  fid?: string;
  page?: number;
  pageSize?: number;
  gizmo?: Gizmo;
  gizmoConfig?: IGizmo;
  context: {
    apiUrl: string;
    user: IUserV2;
    dao: Dao;
  };
}

/**
 * Get index data from the Flowy servers
 *
 * @param type type of data we're bringing from the server
 * @param {string} fid Flowy id of the root gizmo from which we're retrieving the root gizmo data
 * @param {number} page number of page we're going to retrieve from the server
 * @param {number} pageSize number of elements we're going to bring from the server
 * @param {IGizmo} gizmoConfig: gizmo configuration, it's used to add operations column
 *
 * @returns
 */
function getIndex({
  type,
  fid,
  page,
  pageSize,
  gizmo,
  gizmoConfig,
  context,
}: IParams): Promise<IIndex | undefined> {
  return new Promise<IIndex | undefined>(async (resolve, reject) => {
    const { apiUrl, user, dao } = context;

    if (user.credentials) {
      if (type === 'rootGizmo') {
        const credentials: any = user.credentials;
        let url = `${apiUrl}/main_api/v1/root_gizmo/`;
        if (page !== undefined || pageSize !== undefined) {
          url += '?';
          if (page !== undefined) {
            url += `page=${page}`;
          }
          if (pageSize !== undefined) {
            url += `&page-size=${pageSize}`;
          }
        }

        try {
          const formsIndexResponse = await axios.post(
            url,
            {
              projection: {
                title: 1,
                permissions: 1,
              },
            },
            {
              headers: {
                ...credentials,
              },
            }
          );

          if (formsIndexResponse.status === 200) {
            const { forms } = formsIndexResponse.data;

            const total = +formsIndexResponse.headers['x-total'];
            const pageFromServer = +formsIndexResponse.headers['x-page'];
            const pageSizeFromServer = +formsIndexResponse.headers[
              'x-per-page'
            ];

            // TODO: pagination data

            const index = generateRootGizmosIndex({
              forms,
              total,
              pageFromServer,
              pageSizeFromServer,
              gizmoConfig,
            });
            return resolve(index);
          }
        } catch (e) {
          console.log('getIndex.rootGizmo*-*-*-*-*-*-*-*-*-*-*-*-*-e: ', e);
          console.log('j: ', JSON.stringify(e));
          console.log('e: ', JSON.stringify(e.response));

          if (
            (e.code === 'ERR_BAD_RESPONSE' || e.code === 'ERR_NETWORK') &&
            dao.getDatabase()?.getType() !== 'memory'
          ) {
            // If we had a network error we're going to try to get the data from the database
            try {
              const savedForms = await dao.getDatabase()?.search({
                type: 'forms',
                params: {},
              });
              const forms = savedForms?.map((f: any) => ({
                // TODO: we should add the permissions to the form
                pemissions: {
                  answer: true,
                  show: true,
                  review: false,
                },
                ...f.formGizmo,
              }));
              const index = generateRootGizmosIndex({
                forms,
                total: forms?.length || 0,
                pageFromServer: 1,
                pageSizeFromServer: forms?.length || 0,
                gizmoConfig,
              });

              return resolve(index);
            } catch (e) {}
          }
        }

        return resolve(undefined);
      } else if (type === 'rootGizmoData' && fid) {
        const credentials: any = user.credentials;

        const columns: IIndexColumn[] = [];
        const forms = (
          await dao.getRootGizmoConfigs({
            fid,
          })
        ).filter(f => f.fid === fid);

        let label = '';
        let createButtonLabel = '';
        let showStatus = gizmoConfig?.ops?.index?.registers?.showStatus;
        let showUser = false;
        const features: ISingleFeature[] = [];

        let locationField: IGizmo | undefined;
        for (let i = 0; i < forms.length; i++) {
          const form = forms[i];
          const flattenedGizmoConfigs = flattenGizmoConfigs({
            gizmos: form.gizmos!,
          });
          const columnGizmos = flattenedGizmoConfigs.filter(
            g => g.ops?.indexColumn?.isDisplayed
          );
          label = form.label;
          createButtonLabel =
            form.ops?.form?.registersIndex?.createButton?.label || '';

          if (i === 0) {
            // If we're on the first form (the parent one) we're going to look for a location field
            locationField = flattenedGizmoConfigs.find(
              g => g.type === 'location_field'
            );
          }

          if (
            form.ops?.form?.registersIndex?.statusColumn?.display
              ?.isDisplayed !== undefined
          ) {
            showStatus =
              form.ops?.form?.registersIndex?.statusColumn?.display
                ?.isDisplayed;
          }
          if (
            form.ops?.form?.registersIndex?.userColumn?.display?.isDisplayed !==
            undefined
          ) {
            showUser =
              form.ops?.form?.registersIndex?.userColumn?.display?.isDisplayed;
          }

          if (
            form.ops?.form?.registersIndex?.exportButton?.display
              ?.isDisplayed !== undefined
          ) {
            let isDisplayed =
              form.ops?.form?.registersIndex?.exportButton.display.isDisplayed;
            const fluxScript =
              form.ops?.form?.registersIndex?.exportButton.display.flux;

            if (fluxScript && gizmo) {
              const flux = gizmo.getFlux();

              if (flux) {
                const result = await flux.evaluate({
                  script: fluxScript,
                });
                if (result !== undefined && typeof result === 'boolean') {
                  isDisplayed = result;
                }
              }
            }
            features.push({
              path: 'index.exportButton.isDisplayed',
              value: isDisplayed,
            });
          }

          if (columnGizmos) {
            for (const columnGizmo of columnGizmos) {
              const column: IIndexColumn = {
                fid: columnGizmo.fid,
                label: columnGizmo.label,
                gizmo: columnGizmo,
              };
              columns.push(column);
            }
          }
        }

        if (showUser && forms.length === 1) {
          const userColumn: IIndexColumn = {
            fid: 'cU',
            label: 'Usuario',
            type: 'user',
          };

          columns.push(userColumn);
        }

        if (showStatus && forms.length === 1) {
          const statusColumn: IIndexColumn = {
            fid: 'cS',
            label: 'Status',
            type: 'status',
            status: forms[0].status,
          };

          columns.unshift(statusColumn);
        }

        const registersIndexUrl = `${apiUrl}/main_api/v1/root_gizmo/data/${fid}`;
        try {
          const registersIndexResponse = await axios.post(
            registersIndexUrl,
            {
              page,
              pageSize,
            },
            {
              headers: {
                ...credentials,
              },
            }
          );

          if (registersIndexResponse.status === 200) {
            const { filledForms } = registersIndexResponse.data;
            const total = +registersIndexResponse.headers['x-total'];
            const pageFromServer = +registersIndexResponse.headers['x-page'];
            const pageSizeFromServer = +registersIndexResponse.headers[
              'x-per-page'
            ];

            if (filledForms && Array.isArray(filledForms)) {
              const database = dao.getDatabase();
              if (database && database.getType() !== 'memory') {
                try {
                  const params: any = { formId: fid };
                  if (user?.id) {
                    params['userId'] = user.id;
                  }
                  let savedRegisters = await database.search({
                    type: 'filledForms',
                    params,
                  });

                  for (const filledForm of filledForms) {
                    const savedRegister:
                      | IRealmFilledForm
                      | undefined = savedRegisters?.find(
                      (r: any) => r.fid === filledForm.filledFormId
                    );

                    if (savedRegister) {
                      savedRegisters = savedRegisters.filter(
                        (r: any) => r.fid !== filledForm.filledFormId
                      );

                      if (!savedRegister.updated) {
                        try {
                          // If the register is not updated we're going to delete it
                          // from the database
                          await dao.removeRootGizmoData({
                            rootGizmoData: [savedRegister.gizmoData],
                          });

                          continue;
                        } catch (e) {}
                      }

                      filledForm.saved = true;
                      // TODO: should we modify all the filled form object?
                      filledForm.answers = savedRegister.gizmoData.answers;
                      filledForm.status = savedRegister.gizmoData.status;
                    }
                  }

                  if (savedRegisters.length > 0) {
                    // This are the registers that are not in the server yet. We're going to put
                    // them at the beginning
                    const firstRows = savedRegisters.map((r: any) => ({
                      ...r.gizmoData,
                      saved: true,
                    }));

                    filledForms.unshift(...firstRows);
                  }
                } catch (e) {}
              }

              const index = generateRootGizmoDataIndex({
                fid,
                filledForms,
                total,
                pageFromServer,
                pageSizeFromServer,
                columns,
                forms,
                locationField,
                label,
                createButtonLabel,
                gizmoConfig,
                gizmo,
              });

              if (features.length > 0 && gizmo) {
                gizmo.setFeatures({
                  singleFeatures: features,
                });
              }
              return resolve(index);
            }
          }
        } catch (e) {
          console.log('getIndex.rootGizmoData*-*-*-*-*-*-*-*-*-*-*-*-*-e: ', e);
          console.log('j.e: ', JSON.stringify(e));

          if (
            (e.code === 'ERR_BAD_RESPONSE' || e.code === 'ERR_NETWORK') &&
            dao.getDatabase()?.getType() !== 'memory'
          ) {
            const params: any = { formId: fid };
            if (user?.id) {
              params['userId'] = user.id;
            }

            const savedFilledForms = await dao.getDatabase()?.search({
              type: 'filledForms',
              params,
            });
            const filledForms = savedFilledForms?.map((f: any) => ({
              ...f.gizmoData,
              saved: true,
            }));

            if (filledForms) {
              // TODO: handle pagination of local filled forms
              const index = await generateRootGizmoDataIndex({
                fid,
                filledForms,
                total: filledForms?.length || 0,
                pageFromServer: 1,
                pageSizeFromServer: filledForms?.length || 0,
                columns,
                forms,
                locationField,
                label,
                createButtonLabel,
                gizmoConfig,
                gizmo,
              });

              return resolve(index);
            }
          }
        }

        resolve(undefined);
      }
    } else {
      const error: IFlowyError = {
        origin: 'dao',
        code: 'user.not-auth',
        message: 'El usuario no está autenticado.',
      };

      reject(error);
    }
  });
}

interface IGenerateRootGizmosIndexParams {
  forms: any;
  total: number;
  pageFromServer: number;
  pageSizeFromServer: number;
  gizmoConfig?: IGizmo;
}

/**
 * Generate the index object from the root gizmos data, a.k.a., forms
 */
function generateRootGizmosIndex({
  forms,
  total,
  pageFromServer,
  pageSizeFromServer,
  gizmoConfig,
}: IGenerateRootGizmosIndexParams): IIndex {
  const labelColumn: IIndexColumn = {
    fid: 'c1',
    label: 'Nombre formato',
    path: 'title',
  };

  const index: IIndex = {
    fid: 'fid',
    pagination: {
      page: pageFromServer,
      pageSize: pageSizeFromServer,
      total,
    },
    columns: [labelColumn],
    rows: forms,
  };

  if (
    gizmoConfig?.ops?.index?.operations &&
    Array.isArray(gizmoConfig.ops.index.operations)
  ) {
    const operationsColumn: IIndexColumn = {
      fid: 'c2',
      label: 'Ops',
      type: 'operations',
      operations: [],
    };

    for (const operation of gizmoConfig.ops.index.operations) {
      operationsColumn.operations?.push(operation);
    }
    index.columns.push(operationsColumn);
  }

  return index;
}

interface IGenerateRootGizmoDataIndexParams {
  fid: string;
  filledForms: any[];
  total: number;
  pageFromServer: number;
  pageSizeFromServer: number;
  columns: IIndexColumn[];
  forms: IGizmo[];
  locationField: IGizmo | undefined;
  label: string;
  createButtonLabel: string;
  gizmoConfig?: IGizmo;
  gizmo?: Gizmo;
}

async function generateRootGizmoDataIndex({
  fid,
  filledForms,
  total,
  pageFromServer,
  pageSizeFromServer,
  columns,
  forms,
  locationField,
  label,
  createButtonLabel,
  gizmoConfig,
  gizmo,
}: IGenerateRootGizmoDataIndexParams): Promise<IIndex> {
  const index: IIndex = {
    fid,
    pagination: {
      page: pageFromServer,
      pageSize: pageSizeFromServer,
      total,
    },
    columns,
    rootGizmoConfigs: forms,
    rows: filledForms,
  };

  if (locationField) {
    // If we have a location field we're going to enabled map and table types
    // so two buttons are rendered on the index gizmo
    index.types = {
      table: true,
      map: true,
    };
    index.type = 'table';
  }

  if (label) {
    index.label = label;
  }

  if (createButtonLabel) {
    index.createButton = {
      label: createButtonLabel,
    };
  }

  if (
    gizmoConfig?.ops?.index?.operations &&
    Array.isArray(gizmoConfig.ops.index.operations)
  ) {
    const operationsColumn: IIndexColumn = {
      fid: 'c2',
      label: 'Ops',
      type: 'operations',
      operations: [],
    };

    for (const operation of gizmoConfig.ops.index.operations) {
      let addOperation = true;

      if (operation.flux && gizmo) {
        const flux = gizmo.getFlux();

        if (flux) {
          const result = await flux.evaluate({
            script: operation.flux,
          });

          if (result !== undefined && typeof result === 'boolean') {
            addOperation = result;
          }
        }
      }

      if (addOperation) {
        operationsColumn.operations?.push(operation);
      }
    }

    index.columns.push(operationsColumn);
  }

  return index;
}

export default getIndex;
